import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, KeyRound, Plug, RefreshCw, Upload } from 'lucide-react';
import { Button, ConfirmDialog, Skeleton } from '@bliks-io/ui';
import type { DataSourceKind, DataSyncSourceRuns } from '@bliks-io/contracts';
import { useDataSyncStatus } from '@/api/queries/useDataSync';
import {
  useDownloadDataSyncExcelRows,
  useStartExcelDataSync,
  useStartPersonioDataSync,
  useUnlinkPersonio
} from '@/api/mutations/useDataSync';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { downloadDataSyncWorkbook } from '@/lib/excel';
import { formatDate } from '@/lib/formatters';
import { QueryErrorRetry } from '@/components/query-error-retry/QueryErrorRetry';
import { DataSyncDialog } from './DataSyncDialog';
import { PersonioLinkDialog } from './PersonioLinkDialog';
import { readExcelUpload, refusalProblem, type UploadProblem } from './dataSyncExcel';
import { ExcelSourceRow, PersonioSourceRow } from './SourceRows';

/** The Excel and Personio rows with their actions. */
export function DataSyncSettings() {
  const { t } = useTranslation();
  const { data: status, isError, isFetching, refetch } = useDataSyncStatus();
  const [dialogRunId, setDialogRunId] = useState<string | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [personioLinkOpen, setPersonioLinkOpen] = useState(false);
  const [uploadProblem, setUploadProblem] = useState<UploadProblem | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const startExcel = useStartExcelDataSync();
  const startPersonio = useStartPersonioDataSync();
  const unlink = useUnlinkPersonio();
  const downloadRows = useDownloadDataSyncExcelRows();

  if (!status) {
    return isError ? (
      <QueryErrorRetry onRetry={() => void refetch()} loading={isFetching} className="py-8" />
    ) : (
      <Skeleton className="h-8 w-full max-w-md" />
    );
  }

  const downloadExcel = () =>
    downloadRows.mutate(undefined, {
      onSuccess: downloadDataSyncWorkbook,
      onError: (error) => toastApiError(error, 'errors.unknown')
    });

  const uploadExcel = async (file: File) => {
    setUploadProblem(null);
    setIsParsing(true);
    const read = await readExcelUpload(file).catch((error: unknown) => {
      console.error(error);
      return null;
    });
    setIsParsing(false);
    if (!read) {
      toast.error(t('common.excelUpload.parseFailed'));
      return;
    }
    if (!('request' in read)) {
      setUploadProblem(read);
      return;
    }

    startExcel.mutate(read.request, {
      onSuccess: (run) => setDialogRunId(run.id),
      onError: (error) => {
        const problem = refusalProblem(error, read.sheets);
        if (problem) setUploadProblem(problem);
        else toastApiError(error, 'errors.unknown');
      }
    });
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again uploads it again.
    event.target.value = '';
    if (file) void uploadExcel(file);
  };

  const startPersonioSync = () =>
    startPersonio.mutate(undefined, {
      onSuccess: (run) => setDialogRunId(run.id),
      onError: (error) => toastApiError(error, 'errors.unknown')
    });

  const confirmUnlink = () =>
    unlink.mutate(undefined, {
      onSuccess: () => setUnlinkOpen(false),
      onError: (error) => toastApiError(error, 'errors.unknown')
    });

  const { personio, excel, openRun } = status;
  // A start is refused while the tenant's open run reads or applies; one in review it replaces.
  const isRunBusy = openRun?.status === 'reading' || openRun?.status === 'applying';
  // The status shows another start's run only once that start returns.
  const isStarting = isParsing || startExcel.isPending || startPersonio.isPending;
  const reviewRunOf = (kind: DataSourceKind) =>
    openRun?.status === 'review' && openRun.sourceKind === kind ? openRun.id : null;
  const continueButton = (kind: DataSourceKind) => {
    const runId = reviewRunOf(kind);
    return (
      runId && (
        <Button variant="primary" size="small" onClick={() => setDialogRunId(runId)}>
          {t('dataSync.settings.continueSync')}
        </Button>
      )
    );
  };
  // One primary action per row; an open review outranks starting a new one.
  const mainAction = (kind: DataSourceKind) => (reviewRunOf(kind) ? 'secondary' : 'primary');

  return (
    <>
      <div className="flex flex-col gap-8">
        <div className="space-y-3">
          <ExcelSourceRow
            description={excel.lastRun && <LastSync source={excel} onOpen={setDialogRunId} />}
            actions={
              <>
                {continueButton('excel')}
                <Button
                  variant="secondary"
                  size="small"
                  loading={downloadRows.isPending}
                  onClick={downloadExcel}
                >
                  <Download className="h-4 w-4" />
                  {t('dataSync.settings.downloadExcel')}
                </Button>
                <Button
                  variant={mainAction('excel')}
                  size="small"
                  loading={isParsing || startExcel.isPending}
                  disabled={isRunBusy || isStarting}
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                  {t('dataSync.settings.uploadExcel')}
                </Button>
              </>
            }
          />

          {uploadProblem && (
            <div role="alert" className="text-sm text-danger">
              <p>{uploadProblem.message}</p>
              {uploadProblem.rows.length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {uploadProblem.rows.map((row) => (
                    <li key={row}>{row}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold leading-6">{t('dataSync.settings.connections')}</h2>
          <PersonioSourceRow
            connected={Boolean(personio)}
            description={personio && <LastSync source={personio} onOpen={setDialogRunId} />}
            actions={
              personio ? (
                <>
                  {continueButton('personio')}
                  <Button
                    variant={mainAction('personio')}
                    size="small"
                    loading={startPersonio.isPending}
                    disabled={isRunBusy || isStarting}
                    onClick={startPersonioSync}
                  >
                    <RefreshCw className="h-4 w-4" />
                    {t('dataSync.settings.startSync')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setPersonioLinkOpen(true)}
                  >
                    <KeyRound className="h-4 w-4" />
                    {t('dataSync.settings.changeCredentials')}
                  </Button>
                  <Button variant="ghost" size="small" onClick={() => setUnlinkOpen(true)}>
                    {t('dataSync.settings.unlink')}
                  </Button>
                </>
              ) : (
                <Button variant="secondary" size="small" onClick={() => setPersonioLinkOpen(true)}>
                  <Plug className="h-4 w-4" />
                  {t('dataSync.settings.connect')}
                </Button>
              )
            }
          />
        </section>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        aria-label={t('dataSync.settings.uploadExcel')}
        onChange={onFileChange}
      />

      {dialogRunId && (
        <DataSyncDialog
          runId={dialogRunId}
          open
          onOpenChange={(open) => !open && setDialogRunId(null)}
        />
      )}

      <PersonioLinkDialog open={personioLinkOpen} onOpenChange={setPersonioLinkOpen} />

      <ConfirmDialog
        open={unlinkOpen}
        onOpenChange={setUnlinkOpen}
        variant="danger"
        title={t('dataSync.settings.unlinkConfirm.title')}
        description={t('dataSync.settings.unlinkConfirm.description')}
        confirmLabel={t('dataSync.settings.unlink')}
        onConfirm={confirmUnlink}
        isPending={unlink.isPending}
      />
    </>
  );
}

/** A source's last sync: when its last run was applied, opening its newest finished run. */
function LastSync({
  source: { lastSyncAt, lastRun },
  onOpen
}: {
  source: DataSyncSourceRuns;
  onOpen: (runId: string) => void;
}) {
  const { t } = useTranslation();
  if (!lastSyncAt && !lastRun) return null;

  const label = lastSyncAt
    ? t('dataSync.settings.lastSync', { date: formatDate(lastSyncAt) })
    : t('dataSync.settings.noSyncApplied');

  return lastRun ? (
    <button
      type="button"
      className="text-muted-foreground underline-offset-4 hover:underline"
      onClick={() => onOpen(lastRun.id)}
    >
      {label}
    </button>
  ) : (
    <span className="text-muted-foreground">{label}</span>
  );
}
